import React, { useState, useRef, useEffect } from "react";

export default function ChatModal({
  open,
  minimized,
  onClose,
  onMinimize,
  children,
}) {
  const [size, setSize] = useState({ width: 850, height: 600 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState(false);
  const [resizeType, setResizeType] = useState(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const modalRef = useRef(null);

  useEffect(() => {
    if (!minimized && open) {
      const savedWidth = localStorage.getItem("chatModalWidth");
      const savedHeight = localStorage.getItem("chatModalHeight");

      if (savedWidth && savedHeight) {
        setSize({
          width: parseInt(savedWidth),
          height: parseInt(savedHeight),
        });
      }
    }
  }, [minimized, open]);

  useEffect(() => {
    if (resizing) {
      const handleMouseMove = (e) => {
        if (resizeType === "top-left") {
          const deltaX = startPos.x - e.clientX;
          const deltaY = startPos.y - e.clientY;

          const newWidth = Math.max(startSize.width + deltaX, 320);
          const newHeight = Math.max(startSize.height + deltaY, 240);

          setSize({
            width: newWidth,
            height: newHeight,
          });

          setPosition({
            x: position.x - (newWidth - startSize.width),
            y: position.y - (newHeight - startSize.height),
          });
        } else if (resizeType === "left") {
          const deltaX = startPos.x - e.clientX;
          const newWidth = Math.max(startSize.width + deltaX, 320);

          setSize({
            width: newWidth,
            height: size.height,
          });

          setPosition({
            x: position.x - (newWidth - startSize.width),
            y: position.y,
          });
        } else if (resizeType === "top") {
          const deltaY = startPos.y - e.clientY;
          const newHeight = Math.max(startSize.height + deltaY, 240);

          setSize({
            width: size.width,
            height: newHeight,
          });

          setPosition({
            x: position.x,
            y: position.y - (newHeight - startSize.height),
          });
        }
      };

      const handleMouseUp = () => {
        setResizing(false);
        localStorage.setItem("chatModalWidth", size.width);
        localStorage.setItem("chatModalHeight", size.height);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [resizing, resizeType, startPos, startSize, size, position]);

  const startResize = (e, type) => {
    if (minimized) return;

    e.preventDefault();
    setResizing(true);
    setResizeType(type);
    setStartPos({ x: e.clientX, y: e.clientY });
    setStartSize({ width: size.width, height: size.height });
  };

  if (!open) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div
        ref={modalRef}
        className="bg-white border-2 border-blue-500 shadow-xl shadow-black/30 rounded-lg flex flex-col relative"
        style={{
          width: minimized ? "12rem" : `${size.width}px`,
          height: minimized ? "3rem" : `${size.height}px`,
          minWidth: minimized ? "12rem" : "20rem",
          minHeight: minimized ? "3rem" : "15rem",
          maxWidth: "85vw",
          maxHeight: "85vh",
          overflow: "hidden", // Changed from "visible" to "hidden"
        }}
      >
        {/* Top resize handle */}
        {!minimized && (
          <div
            className="absolute h-1 w-full top-0 cursor-n-resize bg-transparent hover:bg-blue-300"
            onMouseDown={(e) => startResize(e, "top")}
          />
        )}

        {/* Left resize handle */}
        {!minimized && (
          <div
            className="absolute w-1 h-full left-0 cursor-w-resize bg-transparent hover:bg-blue-300"
            onMouseDown={(e) => startResize(e, "left")}
          />
        )}

        {/* Top-left resize handle */}
        {!minimized && (
          <div
            className="absolute w-4 h-4 top-0 left-0 cursor-nw-resize bg-blue-500 rounded-br"
            onMouseDown={(e) => startResize(e, "top-left")}
          />
        )}

        <div className="flex items-center justify-between bg-blue-500 text-white px-3 py-1">
          <span className="font-semibold text-sm ml-4 animate-pulse">
            {minimized ? "Assistant (Minimized)" : "Assistant 🟢"}
          </span>
          <div className="flex space-x-1">
            <button onClick={onMinimize} className="focus:outline-none">
              {minimized ? "▲" : "▼"}
            </button>
            <button onClick={onClose} className="focus:outline-none">
              ❌
            </button>
          </div>
        </div>

        {!minimized && (
          <div className="flex-1 p-2 bg-gray-50 overflow-auto">{children}</div>
        )}
      </div>
    </div>
  );
}
